/**
 * OnboardingStep1Page — user type selection (/onboarding/1).
 * On completion, navigates to /onboarding/2 (student/parent) or
 * completes onboarding immediately for teachers.
 */
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/components/ui/use-toast';
import {
  useOnboardingStep1,
  useOnboardingStep3,
  useCurrentUser,
  OnboardingStep1Request,
} from '@/lib/api/auth';
import { getErrorMessage } from '@/lib/api/client';
import OnboardingStep1UserType from '@/components/onboarding/OnboardingStep1UserType';

export default function OnboardingStep1Page() {
  const navigate = useNavigate();
  const { toast } = useToast();

  const { data: user, isLoading: userLoading } = useCurrentUser();
  const step1Mutation = useOnboardingStep1();
  const step3Mutation = useOnboardingStep3();

  useEffect(() => {
    if (!userLoading && !user) {
      navigate('/login', { replace: true });
    }
  }, [user, userLoading, navigate]);

  useEffect(() => {
    if (user && user.is_verified && user.role) {
      const dest =
        user.role === 'teacher' || user.role === 'admin'
          ? '/teacher'
          : user.role === 'student'
          ? '/student'
          : '/parent';
      navigate(dest, { replace: true });
    }
  }, [user, navigate]);

  const handleNext = async (data: OnboardingStep1Request) => {
    try {
      await step1Mutation.mutateAsync(data);

      if (data.user_type === 'teacher') {
        // Teachers have no profile or goals step — complete immediately
        const response = await step3Mutation.mutateAsync({
          goals: [],
          data_use_consent: true,
          terms_conditions_consent: true,
          parental_permission: false,
        });
        toast({ title: 'Welcome to Oracy!', description: 'Your teacher account is ready.' });
        const finalRole = response.user.role || 'teacher';
        navigate(finalRole === 'admin' ? '/admin' : '/teacher', { replace: true });
      } else {
        navigate('/onboarding/2', { state: { userType: data.user_type } });
      }
    } catch (error: unknown) {
      throw new Error(getErrorMessage(error));
    }
  };

  if (userLoading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return <OnboardingStep1UserType onNext={handleNext} isLoading={step1Mutation.isPending || step3Mutation.isPending} />;
}
